import React, {useEffect, useMemo, useState} from 'react';
import PropTypes from 'prop-types';
import cn from 'classnames';
import Select from '@material-ui/core/Select';
import {useDispatch, useSelector} from 'react-redux';
import moment from 'moment/moment';
import ListItem from '@material-ui/core/ListItem';
import MenuItem from '@material-ui/core/MenuItem';
import {
  getBogoItems,
  getCurrentDiscounts,
  getEmailMarketingToggleError,
  getEmailMarketingTrialDate,
  getIsEmailMarketingTurnOffModalOpen,
  getLoadingBogoItems
} from 'store/discountsV2/selectors';
import {useHistory, useLocation} from 'react-router-dom';
import {
  useDaysPickerContext,
  useDaysPickerContextUpdate
} from 'routes/App/discountsV2/DiscountWizard/DateAvailability/context/DaysPickerContext';
import {
  CLEAR_DISCOUNT,
  GET_BOGO_ITEMS,
  OPEN_EMAIL_MARKETING_TURN_OFF_MODAL,
  SET_DISCOUNT_MODAL,
  SET_EMAIL_MARKETING_TOGGLE_ERROR
} from 'store/discountsV2/action';
import DialogContainer from 'components/DialogContainer/styles';
import DialogClose from 'components/DialogClose/styles';
import {
  DashboardField,
  DashboardFieldDatepicker,
  DashboardFieldInner,
  DashboardFieldLabel,
  DashboardFields,
  DashboardInnerDescription,
  DialogBody,
  FieldInput,
  FieldInputDollar,
  FieldLabel
} from 'routes/App/dashboard/styles';
import Dialog from '@material-ui/core/Dialog';
import DialogTitle from 'components/DialogTitle/styles';
import DialogSubTitle from 'components/DialogSubTitle/styles';
import {Box, Checkbox, FormControl, FormControlLabel} from '@material-ui/core';
import {
  DISCOUNT_POSTHOG_EVENTS,
  DISCOUNT_STATUS,
  DISCOUNT_TYPE
} from 'helpers/constants/discountsV2';
import {ErrorSign, ErrorSignWrapper} from 'components/errorSign/errorSign';
import {ButtonInfo, ButtonPrimary} from 'components/buttons';
import {twoDecimalsFormatter} from 'helpers/functionUtils/helpFunctions';
import {
  DiscountsModalButton,
  DiscountsModalSwitch
} from 'routes/App/discountsV2/styles';
import SwitchS2E from 'components/Switch';

import isEmailMarketingTrialExpired from 'helpers/functionUtils/emailMarketing';
import EmailMarketingTurnOffModal from 'routes/App/discountsV2/DiscountsList/Modals/EmailMarketingTurnOffModal';
import DatepickerS2E from 'components/Datepicker';
import DaysPickerContainer from 'routes/App/discountsV2/DiscountWizard/DateAvailability/DaysPickerContainer';
import {
  convertScheduleFromServiceFormat,
  convertScheduleToServiceFormat
} from 'routes/App/discountsV2/DiscountWizard/DateAvailability/helpers';
import SelectWrapper from 'components/select/SelectComponentStyles';
import Loading from 'components/shared/Utils/Loading';
import {FlexContainer, FlexItem} from 'components/layout';
import SearchField from 'components/fields/SearchField';

const ItemSettingsModal = (props) => {
  const {
    formik,
    discount,
    enabledDiscount,
    setEnabledDiscount,
    enabledDate,
    setEnabledDate,
    buttonStatus,
    allowedServiceFeatures,
    enabledCustomAvailability,
    setEnabledCustomAvailability,
    enabledAdvancedSettings,
    setEnabledAdvancedSettings,
    isOpen,
    onCloseAction
  } = props;

  const dispatch = useDispatch();
  const {activeLocation, userData} = useSelector((state) => state.user);
  const isEmailMarketingToggleError = getEmailMarketingToggleError();
  const isEmailMarketingTurnOffModalOpen = getIsEmailMarketingTurnOffModalOpen();
  const emailMarketingTrialDate = getEmailMarketingTrialDate();
  const {Timezone} = useSelector((state) => state.user.activeLocation);
  const visualChecked = !formik.values.isPromotional;
  const specificItems = getBogoItems();
  const currentDiscounts = getCurrentDiscounts();
  const loadingSpecificItems = getLoadingBogoItems();
  const [selectedSpecificItems, setSelectedSpecificItems] = useState([]);
  const [filteredSpecificItems, setFilteredSpecificItems] = useState([]);
  const [search, setSearch] = useState('');
  const [openSelect, setOpenSelect] = useState(false);

  const daysPickerContext = useDaysPickerContext();
  const setDaysPickerContext = useDaysPickerContextUpdate();

  const discountMap = useMemo(() => {
    const map = new Map();

    if (!currentDiscounts || currentDiscounts.length === 0) {
      return map;
    }

    currentDiscounts.forEach((currentDiscount) => {
      currentDiscount.items.forEach((item) => {
        if (!map.has(item.posId)) {
          map.set(item.posId, []);
        }
        map.get(item.posId).push({
          discountId: currentDiscount.discountId,
          discountName: currentDiscount.discountName
        });
      });
    });
    return map;
  }, [currentDiscounts]);

  const availableItems = useMemo(() => {
    if (!specificItems || specificItems.length === 0) {
      return [];
    }

    return specificItems.filter((item) => {
      const existingDiscounts = discountMap.get(item.posId) || [];
      const isUsedInCurrent = existingDiscounts.some(
        (entry) => entry.discountId === discount?.id
      );
      const isUsedInOthers = existingDiscounts.some(
        (entry) => entry.discountId !== discount?.id
      );
      const isSelectedInCurrent = selectedSpecificItems.includes(item.posId);

      return !isUsedInOthers || isSelectedInCurrent || isUsedInCurrent;
    });
  }, [specificItems, discountMap, discount?.id, selectedSpecificItems]);

  useEffect(() => {
    if (specificItems?.length > 0) {
      if (formik?.values?.specificItems?.discounted?.length > 0) {
        setSelectedSpecificItems(
          formik.values.specificItems.discounted?.map((item) => item?.posId) ||
            []
        );
      } else {
        setSelectedSpecificItems([]);
      }
    }
  }, [specificItems, formik?.values?.specificItems?.discounted]);

  useEffect(() => {
    if (availableItems?.length > 0) {
      if (search) {
        setFilteredSpecificItems(
          availableItems.filter((item) =>
            item.name.toLowerCase().includes(search.toLowerCase())
          )
        );
      } else {
        setFilteredSpecificItems(availableItems);
      }
    } else {
      setFilteredSpecificItems([]);
    }
  }, [availableItems, search]);

  useEffect(() => {
    if (!specificItems || specificItems.length === 0) {
      dispatch(GET_BOGO_ITEMS());
    }

    return () => {
      dispatch(CLEAR_DISCOUNT());
    };
  }, []);

  const handleSearchSpecificItems = (value) => {
    setSearch(value);
  };

  const mapPosIdsToDiscountedItems = (posIds) => {
    return posIds
      .map((posId) => {
        const item = specificItems.find((el) => el.posId === posId);
        return item ? {...item, type: 'Item'} : null;
      })
      .filter(Boolean);
  };

  const handleSpecificItemsSelect = (value) => {
    if (value) {
      const isSelected = selectedSpecificItems?.includes(value.posId);

      if (isSelected) {
        const newSelected = selectedSpecificItems.filter(
          (id) => id !== value.posId
        );
        setSelectedSpecificItems(newSelected);
        const newDiscountedItems = mapPosIdsToDiscountedItems(newSelected);
        formik.setFieldValue('specificItems.discounted', newDiscountedItems);
      } else {
        if (selectedSpecificItems.length >= 10) {
          return;
        }

        const newSelected = [...selectedSpecificItems, value.posId];
        setSelectedSpecificItems(newSelected);
        const newDiscountedItems = mapPosIdsToDiscountedItems(newSelected);
        formik.setFieldValue('specificItems.discounted', newDiscountedItems);
      }
    }
  };

  const handleOpen = () => {
    setOpenSelect(!openSelect);
  };

  const handleSearchClear = () => {
    setSearch('');
  };

  useEffect(() => {
    if (isOpen && discount && window?.posthog) {
      window?.posthog.capture(DISCOUNT_POSTHOG_EVENTS.MODAL_OPENED, {
        mode: discount?.id ? 'edit' : 'new',
        discount_type: 'item_level',
        location_id: activeLocation?._id,
        location_name: activeLocation?.BusinessName || activeLocation?.FullName,
        user_id: userData?._id,
        user_email: userData?.email,
        timestamp: moment().utc().format()
      });
    }
  }, [isOpen, discount]);

  return (
    <Dialog
      open={isOpen}
      onClose={onCloseAction}
      disableEnforceFocus
      disableRestoreFocus
      scroll='body'
    >
      <DialogContainer className='DialogLgDiscounts'>
        <DialogClose onClick={onCloseAction} />
        <Box>
          <DialogTitle className='marginBottomLg'>
            {discount?.id ? 'Edit ' : 'Create New '}Discount - Item-Level
          </DialogTitle>
        </Box>
        <DialogBody>
          <DashboardFields>
            <DashboardField className='dFlex discountV2Name'>
              <DashboardFieldInner className='w60FLex DiscountsV2 FieldRelative'>
                <FieldLabel
                  className={cn('FieldLabelLg mBottomMd', {
                    isDisabled: discount.status === DISCOUNT_STATUS.ARCHIVED
                  })}
                >
                  Discount Name <span className='redText'>*</span>
                </FieldLabel>
                <FieldInput
                  className={cn('', {
                    error: formik.errors?.name && formik.touched?.name,
                    isDisabled: discount.status === DISCOUNT_STATUS.ARCHIVED
                  })}
                  name='name'
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  value={formik.values.name}
                  placeholder='Discount Name'
                />
                <ErrorSignWrapper className='ErrorSignWrapper top3'>
                  {formik.errors?.name && formik.touched?.name ? (
                    <ErrorSign>{formik.errors.name}</ErrorSign>
                  ) : null}
                </ErrorSignWrapper>
              </DashboardFieldInner>
            </DashboardField>
            <DashboardField className='dFlex'>
              <DashboardFields className='marginMinus13'>
                <DashboardField className='wAuto w50mob w50 paddingT14'>
                  <FieldLabel className='FieldLabelLg mBottomMd'>
                    Discount Type <span className='redText'>*</span>
                  </FieldLabel>
                  <SelectWrapper className='SelectMd w100'>
                    <FormControl className='select-formcontrol'>
                      <Select
                        value={formik.values?.rate?.type}
                        name='rate.type'
                        onChange={(e) => {
                          formik.setFieldValue('rate.type', e.target.value);
                        }}
                        className={cn('', {
                          error:
                            formik.errors?.rate?.type &&
                            formik.touched?.rate?.type,
                          isDisabled:
                            discount.status === DISCOUNT_STATUS.ARCHIVED
                        })}
                        MenuProps={{
                          anchorOrigin: {
                            vertical: 'bottom',
                            horizontal: 'left'
                          },
                          transformOrigin: {
                            vertical: 'top',
                            horizontal: 'left'
                          },
                          getContentAnchorEl: null
                        }}
                      >
                        <MenuItem value={DISCOUNT_TYPE.FIXED}>$</MenuItem>
                        <MenuItem value={DISCOUNT_TYPE.PERCENTAGE}>%</MenuItem>
                      </Select>
                    </FormControl>
                  </SelectWrapper>
                  <ErrorSignWrapper topPosition='-11%'>
                    {formik.errors?.rate?.type && formik.touched?.rate?.type ? (
                      <ErrorSign>{formik.errors?.rate?.type}</ErrorSign>
                    ) : null}
                  </ErrorSignWrapper>
                </DashboardField>
                <DashboardField className='wAuto w50mob w50'>
                  <FieldLabel
                    className={cn('FieldLabelLg mBottomMd', {
                      isDisabled: discount.status === DISCOUNT_STATUS.ARCHIVED
                    })}
                  >
                    Discount Value <span className='redText'>*</span>
                  </FieldLabel>
                  <FieldInput
                    className={cn('', {
                      error:
                        formik.errors?.rate?.value &&
                        formik.touched?.rate?.value,
                      isDisabled: discount.status === DISCOUNT_STATUS.ARCHIVED
                    })}
                    name='rate.value'
                    type='text'
                    onChange={(e) =>
                      formik.setFieldValue(
                        'rate.value',
                        twoDecimalsFormatter(
                          e.target.value,
                          formik.values?.rate?.value,
                          formik.values?.rate?.type === DISCOUNT_TYPE.PERCENTAGE
                            ? 99
                            : 9999.99
                        ) || ''
                      )
                    }
                    onBlur={(e) => {
                      const lastElement = e.target.value.slice(-1)[0];
                      if (lastElement === '.') {
                        formik.setFieldValue(
                          'rate.value',
                          e.target.value.slice(0, -1)
                        );
                      }
                    }}
                    value={formik.values?.rate?.value}
                    placeholder=''
                  />
                  <ErrorSignWrapper topPosition='-9%'>
                    {formik.errors?.rate?.value &&
                    formik.touched?.rate?.value ? (
                      <ErrorSign>{formik.errors?.rate?.value}</ErrorSign>
                    ) : null}
                  </ErrorSignWrapper>
                </DashboardField>
              </DashboardFields>
            </DashboardField>
          </DashboardFields>
          <DashboardField className='discountV2Name paddingL0 paddingR0'>
            <FieldLabel
              className={cn('FieldLabelLg mBottomMd', {
                isDisabled: discount.status === DISCOUNT_STATUS.ARCHIVED
              })}
            >
              Select items (max 10) <span className='redText'>*</span>
            </FieldLabel>
            <SelectWrapper className='SelectMd w100Auto AlignCenter'>
              <FormControl className='select-formcontrol DiscountsV2'>
                <Select
                  value={
                    selectedSpecificItems.length > 0
                      ? selectedSpecificItems
                      : []
                  }
                  name='specificItems.discounted'
                  open={openSelect}
                  multiple
                  displayEmpty
                  onOpen={handleOpen}
                  onClose={handleOpen}
                  onChange={() => {}}
                  className={
                    formik?.errors?.specificItems?.discounted && 'error'
                  }
                  renderValue={(selected) => {
                    if (!selected || selected.length === 0) {
                      return (
                        <span style={{color: '#aaa'}}>
                          Select items (max 10)
                        </span>
                      );
                    }

                    const selectedCount = selected?.length;
                    const selectedNames = selected
                      .slice(0, 3)
                      .map(
                        (item) =>
                          specificItems.find((el) => item === el.posId)?.name
                      )
                      .filter(Boolean)
                      .join(', ');

                    if (selectedCount > 3) {
                      return `${selectedNames} and ${selectedCount - 3} more`;
                    }

                    return selectedNames;
                  }}
                  MenuProps={{
                    anchorOrigin: {
                      vertical: 'bottom',
                      horizontal: 'left'
                    },
                    transformOrigin: {
                      vertical: 'top',
                      horizontal: 'left'
                    },
                    PaperProps: {
                      style: {
                        maxHeight: 220,
                        overflowY: 'auto',
                        maxWidth: 250
                      },
                      className: 'DiscountsV2CustomPaper'
                    },
                    getContentAnchorEl: null
                  }}
                >
                  {loadingSpecificItems ? (
                    <Loading />
                  ) : (
                    [
                      <div
                        key='search-container'
                        role='presentation'
                        style={{
                          position: 'sticky',
                          top: 0,
                          background: 'white',
                          zIndex: 100
                        }}
                      >
                        <FlexContainer>
                          <FlexItem
                            className='padding10 bogoSearch'
                            flex='1 1 auto'
                          >
                            <SearchField
                              placeholder='Search by item'
                              value={search}
                              onChange={(e) =>
                                handleSearchSpecificItems(e.target.value)
                              }
                              onClear={handleSearchClear}
                              isClearIconVisible={search?.length > 0}
                              onKeyDown={(e) => e.stopPropagation()}
                              onKeyUp={(e) => e.stopPropagation()}
                            />
                          </FlexItem>
                        </FlexContainer>
                      </div>,
                      ...filteredSpecificItems?.map((item) => {
                        const isSelected = selectedSpecificItems.includes(
                          item?.posId
                        );
                        const isMaxReached =
                          selectedSpecificItems.length >= 10 && !isSelected;
                        const isItemDisabled = isMaxReached;

                        let titleText = '';
                        if (isMaxReached) {
                          titleText = 'Maximum 10 items allowed';
                        }

                        return (
                          <ListItem
                            className='listItemText'
                            onClick={() => {
                              if (!isItemDisabled) {
                                handleSpecificItemsSelect(item);
                              }
                            }}
                            key={item.posId}
                            value={item.posId}
                            disabled={isItemDisabled}
                            title={titleText}
                          >
                            <Checkbox
                              checked={isSelected}
                              disabled={isItemDisabled}
                              style={{
                                color: '#FF2D38',
                                padding: '0 8px 0 0'
                              }}
                              onClick={(e) => {
                                e.stopPropagation();
                                if (!isItemDisabled) {
                                  handleSpecificItemsSelect(item);
                                }
                              }}
                            />
                            {item.name}
                          </ListItem>
                        );
                      })
                    ]
                  )}
                </Select>
              </FormControl>
            </SelectWrapper>
            <ErrorSignWrapper topPosition='-9%' leftPosition='0'>
              <ErrorSign>
                {formik?.errors?.bogo?.discounted &&
                  formik.errors.bogo.discounted}
              </ErrorSign>
            </ErrorSignWrapper>
          </DashboardField>
          {/* {(allowedServiceFeatures.includes('email_campaigns_active') ||
            (emailMarketingTrialDate &&
              !isEmailMarketingTrialExpired(
                emailMarketingTrialDate,
                Timezone
              ))) && (
            <DiscountsModalSwitch>
              <div className='LeftBlock'>
                <SwitchS2E
                  labelPlacement='start'
                  checked={formik.values?.marketing?.email?.isEnabled}
                  onChange={(e) => {
                    if (!e.target.checked) {
                      dispatch(OPEN_EMAIL_MARKETING_TURN_OFF_MODAL(true));
                    } else {
                      formik.setFieldValue('marketing.email.isEnabled', true);
                    }
                  }}
                  name='marketing.email.isEanbled'
                  isDisabled={discount.status === DISCOUNT_STATUS.ARCHIVED}
                />
                <span className='switcher-text'>
                  {formik.values?.marketing?.email?.isEnabled
                    ? 'Active'
                    : 'Disabled'}
                </span>
              </div>
              <div className='RightBlock'>
                <div className='title'>Include in Email Marketing</div>
                <div className='description'>
                  Display this discount in your email marketing campaigns
                </div>
                <ErrorSignWrapper className='ErrorSignWrapper noWrap'>
                  {formik.errors?.marketing?.email?.isEnabled &&
                  formik.touched?.marketing?.email?.isEnabled ? (
                    <ErrorSign>
                      {formik.errors.marketing.email.isEnabled}
                    </ErrorSign>
                  ) : null}
                </ErrorSignWrapper>
              </div>
            </DiscountsModalSwitch>
          )} */}
          <DiscountsModalSwitch className='mTop17'>
            <div className='LeftBlock'>
              <SwitchS2E
                labelPlacement='start'
                checked={enabledCustomAvailability}
                onChange={(e) => {
                  if (!e.target.checked) {
                    setDaysPickerContext([]);
                    formik.setFieldValue('availability.activatesAtLocal', null);
                    formik.setFieldValue(
                      'availability.deactivatesAtLocal',
                      null
                    );
                    setEnabledDate(false);
                    setEnabledAdvancedSettings(false);
                    formik.setFieldValue('availability.weekly', null);
                  }
                  setEnabledCustomAvailability(e.target.checked);
                }}
                name='enabledCustomAvailability'
                isDisabled={discount.status === DISCOUNT_STATUS.ARCHIVED}
              />
              <span className='switcher-text'>
                {enabledCustomAvailability ? 'Active' : 'Disabled'}
              </span>
            </div>
            <div className='RightBlock'>
              <div className='title'>Custom availability</div>
              <div className='description'>
                Set specific date, dates and hour
              </div>
            </div>
          </DiscountsModalSwitch>
          {enabledCustomAvailability && (
            <>
              <DashboardField className='dFlex w100 DiscountDatepicker paddingR0 paddingL0'>
                <FormControlLabel
                  className='Say2EatCheckbox'
                  control={
                    <Checkbox
                      labelStyle={{color: '#2F3D48'}}
                      iconStyle={{fill: '#FF2D38'}}
                      inputStyle={{color: '#FF2D38'}}
                      style={{
                        color: '#FF2D38',
                        opacity:
                          discount.status === DISCOUNT_STATUS.ARCHIVED
                            ? '0.3'
                            : null
                      }}
                      onChange={(e) => {
                        const {checked} = e.target;
                        setEnabledDate(checked);
                        if (!checked) {
                          formik.setFieldValue(
                            'availability.activatesAtLocal',
                            null
                          );
                          formik.setFieldValue(
                            'availability.deactivatesAtLocal',
                            null
                          );
                        }
                      }}
                      checked={enabledDate}
                      disabled={discount.status === DISCOUNT_STATUS.ARCHIVED}
                    />
                  }
                  label='From'
                />
                <DashboardFieldDatepicker>
                  <DatepickerS2E
                    hideTextFrom
                    disablePast
                    disabled={
                      !enabledDate ||
                      discount.status === DISCOUNT_STATUS.ARCHIVED
                    }
                    value={[
                      formik.values?.availability?.activatesAtLocal
                        ? moment(formik.values.availability.activatesAtLocal)
                            .utc()
                            .format('MM/DD/yy')
                        : null,
                      formik.values?.availability?.deactivatesAtLocal
                        ? moment(formik.values.availability.deactivatesAtLocal)
                            .utc()
                            .format('MM/DD/yy')
                        : null
                    ]}
                    onClose={(value) => {
                      if (value && value.length) {
                        formik.setFieldValue(
                          'availability.activatesAtLocal',
                          moment(value[0])
                            .utcOffset(0, true)
                            .startOf('day')
                            .format()
                        );
                        formik.setFieldValue(
                          'availability.deactivatesAtLocal',
                          moment(value[1])
                            .utcOffset(0, true)
                            .endOf('day')
                            .format()
                        );
                      }
                    }}
                    clearSelectedValue
                  />
                </DashboardFieldDatepicker>
                <ErrorSignWrapper
                  className='ErrorSignWrapper'
                  leftPosition='93px'
                  topPosition='-14%'
                >
                  {formik.errors?.availability?.activatesAtLocal &&
                  formik.touched?.availability?.activatesAtLocal ? (
                    <ErrorSign>
                      {formik.errors.availability.activatesAtLocal}
                    </ErrorSign>
                  ) : null}
                </ErrorSignWrapper>
              </DashboardField>

              <DashboardField className='dFlex w100 DiscountDatepicker paddingR0 paddingL0'>
                <FormControlLabel
                  className='Say2EatCheckbox'
                  control={
                    <Checkbox
                      labelStyle={{color: '#2F3D48'}}
                      iconStyle={{fill: '#FF2D38'}}
                      inputStyle={{color: '#FF2D38'}}
                      style={{
                        color: '#FF2D38',
                        opacity:
                          discount.status === DISCOUNT_STATUS.ARCHIVED
                            ? '0.3'
                            : null
                      }}
                      onChange={(e) => {
                        const {checked} = e.target;
                        setEnabledAdvancedSettings(checked);
                        if (!checked) {
                          formik.setFieldValue('availability.weekly', null);
                          setDaysPickerContext([]);
                        }
                      }}
                      checked={enabledAdvancedSettings}
                      disabled={discount.status === DISCOUNT_STATUS.ARCHIVED}
                    />
                  }
                  label='Advanced settings'
                />
              </DashboardField>

              {enabledAdvancedSettings && (
                <DashboardField className='paddingR0 paddingL0'>
                  <DaysPickerContainer
                    WorkingDays={convertScheduleFromServiceFormat(
                      formik?.values?.availability?.weekly || []
                    )}
                  />
                </DashboardField>
              )}
            </>
          )}
          <DiscountsModalButton>
            <ButtonPrimary
              type='button'
              className={cn('ButtonDashboard', {
                disabled:
                  buttonStatus || discount.status === DISCOUNT_STATUS.ARCHIVED
              })}
              onClick={(e) => {
                e.stopPropagation();

                setEnabledDiscount(false);

                if (
                  formik.values?.rate?.type === DISCOUNT_TYPE.PERCENTAGE &&
                  formik.values?.rate?.value > 95
                ) {
                  formik.setFieldError(
                    'rate.value',
                    'Discount cannot be bigger than 95%!'
                  );
                  return;
                }
                formik.setFieldValue('subtotal.min', 0);
                formik.setFieldValue('promoCode', null);

                formik.setFieldValue(
                  'availability.weekly',
                  enabledAdvancedSettings
                    ? convertScheduleToServiceFormat(daysPickerContext)
                    : null
                );

                formik.handleSubmit(e);
              }}
            >
              <span>Save</span>
            </ButtonPrimary>
          </DiscountsModalButton>
        </DialogBody>
      </DialogContainer>
      {isEmailMarketingTurnOffModalOpen && (
        <EmailMarketingTurnOffModal
          isOpen={isEmailMarketingTurnOffModalOpen}
          setFieldValue={formik.setFieldValue}
        />
      )}
    </Dialog>
  );
};

ItemSettingsModal.propTypes = {
  formik: PropTypes.object.isRequired,
  discount: PropTypes.object.isRequired,
  enabledDiscount: PropTypes.bool.isRequired,
  setEnabledDiscount: PropTypes.func.isRequired,
  enabledDate: PropTypes.bool.isRequired,
  setEnabledDate: PropTypes.func.isRequired,
  buttonStatus: PropTypes.bool.isRequired,
  allowedServiceFeatures: PropTypes.array.isRequired,
  enabledCustomAvailability: PropTypes.bool.isRequired,
  setEnabledCustomAvailability: PropTypes.func.isRequired,
  enabledAdvancedSettings: PropTypes.bool.isRequired,
  setEnabledAdvancedSettings: PropTypes.func.isRequired,
  isOpen: PropTypes.bool.isRequired,
  onCloseAction: PropTypes.func.isRequired
};

export default ItemSettingsModal;
